import {
  getMPDataWithParams,
  postMPDataWithParams,
  putMPDataWithParams,
} from 'src/mentor/services'

const TEMPLATE_BASE = '/api/secure/doctor/medical-certificate-template'
const CERT_BASE = '/api/secure/doctor/medical-certificate'

function isOk(res) {
  return res?.data?.code === 2000 || res?.status === 200
}

/** Role/discipline-filtered templates for the current practitioner. */
export async function fetchCertificateTemplates({ clinicId, discipline } = {}) {
  const res = await getMPDataWithParams(TEMPLATE_BASE, { clinicId, discipline })
  if (!isOk(res)) {
    return { ok: false, templates: [], message: res?.data?.message || 'Failed to load templates' }
  }
  return { ok: true, templates: res?.data?.data?.templates || [] }
}

/** Generate (render server-side + store in S3) and persist a certificate. Returns the saved record. */
export async function generateCertificate(payload, loadingTst) {
  const res = await postMPDataWithParams(`${CERT_BASE}/generate`, payload, {}, loadingTst)
  if (!isOk(res)) {
    return { ok: false, certificate: null, message: res?.data?.message || 'Failed to generate certificate' }
  }
  return { ok: true, certificate: res?.data?.data?.certificate || null }
}

export async function fetchCertificatesForPatient(patientId) {
  const res = await getMPDataWithParams(`${CERT_BASE}/patient/list`, { patientId })
  if (!isOk(res)) {
    return { ok: false, certificates: [], message: res?.data?.message || 'Failed to load certificates' }
  }
  return { ok: true, certificates: res?.data?.data?.certificates || [] }
}

/**
 * Update an issued certificate's view access after issuance — change the policy (Open / Anyone /
 * Restricted) and/or the allowed-viewer whitelist (e.g. to let a specific viewer in later). Issuer
 * only; the patient's own number is always re-added server-side.
 */
export async function updateCertificateAccess(certId, { viewAccessPolicy, allowedViewerMobiles }) {
  const res = await putMPDataWithParams(
    `${CERT_BASE}/${certId}/access`,
    { viewAccessPolicy, allowedViewerMobiles },
    {},
  )
  if (!isOk(res)) {
    return { ok: false, certificate: null, message: res?.data?.message || 'Failed to update access' }
  }
  return { ok: true, certificate: res?.data?.data?.certificate || null }
}

export async function revokeCertificate(certId, reason) {
  const res = await putMPDataWithParams(`${CERT_BASE}/${certId}/revoke`, {}, { reason })
  if (!isOk(res)) {
    return { ok: false, certificate: null, message: res?.data?.message || 'Failed to revoke certificate' }
  }
  return { ok: true, certificate: res?.data?.data?.certificate || null }
}

/**
 * Send an issued certificate to the patient. Default queues delivery (dispatched by the ~15-min
 * batch); pass immediate=true to send right away (bypasses the queue). The patient's contact is
 * resolved server-side and never returned here.
 */
export async function sendCertificateToPatient(certId, immediate = false) {
  const res = await postMPDataWithParams(`${CERT_BASE}/${certId}/send`, {}, { immediate })
  if (!isOk(res)) {
    return { ok: false, message: res?.data?.message || 'Failed to send certificate' }
  }
  return {
    ok: true,
    queued: res?.data?.data?.queued !== false && !immediate,
    certificate: res?.data?.data?.certificate || null,
  }
}
